import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Zap, Send, Sparkles, Loader2, RotateCcw, ArrowDownWideNarrow } from "lucide-react";
import { api } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";

const AIGUILLON_VIDEO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/j5q3avas_gemini_generated_video_cbdd34de.mp4";

const T = {
  fr: { eyebrow: "L'art de bien demander", title: "L'Aiguillon", intro: "Socrate se disait le « taon », l'aiguillon d'Athènes : celui qui pique et réveille les esprits par ses questions. Ici, pas de réponses — dix questions puissantes sur ton sujet, de la plus concrète à la plus vertigineuse.", placeholder: "Un sujet qui te travaille…", go: "Pique-moi", surprise: "Surprends-moi", restart: "Autre sujet", loading: "L'aiguillon s'affûte…", error: "L'Aiguillon s'est émoussé. Réessaie.", from: "De la surface au vertige" },
  en: { eyebrow: "The art of asking well", title: "The Goad", intro: "Socrates called himself the \"gadfly\" of Athens: the one who stings and awakens minds with questions. Here, no answers — ten powerful questions on your topic, from the most concrete to the most dizzying.", placeholder: "A subject on your mind…", go: "Sting me", surprise: "Surprise me", restart: "Another topic", loading: "The goad is sharpening…", error: "The Goad went blunt. Try again.", from: "From surface to vertigo" },
  es: { eyebrow: "El arte de preguntar bien", title: "El Aguijón", intro: "Sócrates se llamaba a sí mismo el «tábano» de Atenas: el que pica y despierta las mentes con preguntas. Aquí, sin respuestas — diez preguntas poderosas sobre tu tema, de la más concreta a la más vertiginosa.", placeholder: "Un tema que te inquieta…", go: "Aguijonéame", surprise: "Sorpréndeme", restart: "Otro tema", loading: "El aguijón se afila…", error: "El Aguijón se desafiló. Inténtalo de nuevo.", from: "De la superficie al vértigo" },
  nl: { eyebrow: "De kunst van het goede vragen", title: "De Prikkel", intro: "Socrates noemde zichzelf de \"horzel\" van Athene: degene die prikt en de geesten wakker maakt met vragen. Hier geen antwoorden — tien krachtige vragen over jouw onderwerp, van de meest concrete tot de meest duizelingwekkende.", placeholder: "Een onderwerp dat je bezighoudt…", go: "Prik me", surprise: "Verras me", restart: "Ander onderwerp", loading: "De prikkel wordt gescherpt…", error: "De Prikkel werd bot. Probeer opnieuw.", from: "Van oppervlakte tot duizeling" },
};

export const Aiguillon = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const c = T[lang] || T.fr;
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async (topic) => {
    setLoading(true);
    try {
      const { data } = await api.get("/aiguillon", { params: { lang, topic: topic || "" } });
      setData(data);
    } catch {
      toast.error(c.error);
    } finally {
      setLoading(false);
    }
  };

  const start = () => { const v = input.trim(); if (!v) return; run(v); };
  const restart = () => { setData(null); setInput(""); };

  return (
    <section className="relative px-6 my-20" data-testid="aiguillon-section">
      <div className="max-w-4xl mx-auto">
        <div className="relative rounded-[2rem] overflow-hidden border border-primary/20 frame-pulse glass">
          <div className="relative z-10 px-6 sm:px-12 py-14 text-center">
            <div className="max-w-2xl mx-auto mb-10">
              <CinematicVideo
                silver
                src={AIGUILLON_VIDEO}
                testId="aiguillon-cinematic-video"
                caption={c.intro}
                fade
                noSound
              />
            </div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/15 border border-primary/40 text-primary text-[11px] uppercase tracking-[0.28em] mb-6" data-testid="aiguillon-badge">
              <Zap size={13} /> {c.eyebrow}
            </span>
            <h2 className="font-display text-gradient-orange text-4xl sm:text-6xl leading-[1.12] pb-[0.08em] mb-4" data-testid="aiguillon-title">{c.title}</h2>
            <p className="text-foreground/65 text-base sm:text-lg max-w-xl mx-auto leading-relaxed italic mb-9">{c.intro}</p>

            {!data && !loading && (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <div className="flex flex-col sm:flex-row items-center gap-3 max-w-lg mx-auto">
                  <input
                    data-testid="aiguillon-input" value={input} onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && start()} placeholder={c.placeholder}
                    className="w-full rounded-full bg-white/[0.04] border border-white/12 focus:border-primary/50 outline-none px-5 py-3.5 text-sm text-foreground/90 transition-colors"
                  />
                  <button onClick={start} data-testid="aiguillon-go" className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm whitespace-nowrap">
                    <Send size={15} /> {c.go}
                  </button>
                </div>
                <button onClick={() => run("")} data-testid="aiguillon-surprise" className="mt-5 inline-flex items-center gap-2 text-primary/85 hover:text-primary text-sm transition-colors">
                  <Sparkles size={15} /> {c.surprise}
                </button>
              </motion.div>
            )}

            {loading && (
              <div className="mt-2 flex flex-col items-center gap-3 text-foreground/60" data-testid="aiguillon-loading">
                <Loader2 size={26} className="animate-spin text-primary" />
                <span className="text-sm">{c.loading}</span>
              </div>
            )}

            <AnimatePresence mode="wait">
              {data && !loading && (
                <motion.div
                  key={data.topic}
                  initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.5 }}
                  className="max-w-2xl mx-auto"
                  data-testid="aiguillon-result"
                >
                  <div className="flex items-center justify-between mb-5 text-xs">
                    <span className="inline-flex items-center gap-1.5 text-primary/70">
                      <ArrowDownWideNarrow size={13} /> {c.from}
                    </span>
                    <button onClick={restart} data-testid="aiguillon-restart" className="inline-flex items-center gap-1.5 text-foreground/45 hover:text-primary transition-colors">
                      <RotateCcw size={13} /> {c.restart}
                    </button>
                  </div>
                  <p className="text-center font-display text-2xl text-gradient-orange mb-6" data-testid="aiguillon-topic">« {data.topic} »</p>
                  <div className="space-y-3 text-left">
                    {(data.questions || []).map((q, i) => {
                      const depth = (data.questions.length > 1) ? i / (data.questions.length - 1) : 0;
                      return (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.07 }}
                          className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4"
                          style={{ marginLeft: `${depth * 28}px`, borderColor: `hsl(28 96% 56% / ${0.12 + depth * 0.35})` }}
                          data-testid={`aiguillon-q-${i}`}
                        >
                          <span className="font-display text-primary/60 text-lg leading-none pt-0.5" style={{ opacity: 0.4 + depth * 0.6 }}>{String(i + 1).padStart(2, "0")}</span>
                          <p className="text-[15px] leading-relaxed text-foreground/85">{q}</p>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
